import React, { useMemo, useState } from 'react';
import { adminCreateCompany, adminUpdateCompany, adminDeleteCompany } from '../../api/admin';
import type { Company, StatusGroup } from '../../types/auth';
import { useToastStore } from '../../store/useToastStore';
import { TH, PERIOD_LABELS, usePaginated, PaginationBar, ConfirmDialog } from './shared';
import CompanyEditor from './CompanyEditor';
import type { CompanySaveData } from './CompanyEditor';

interface Props {
  companies: Company[];
  groups: StatusGroup[];
  onChanged: () => Promise<void>;
}

type EditState = { mode: 'edit'; company: Company } | { mode: 'new' } | { mode: 'duplicate'; template: Company } | null;

export default function CompaniesTab({ companies, groups, onChanged }: Props) {
  const addToast = useToastStore((s) => s.addToast);
  const [editing, setEditing] = useState<EditState>(null);
  const [confirmDelete, setConfirmDelete] = useState<Company | null>(null);
  const [searchQ, setSearchQ] = useState('');

  const filtered = useMemo(() => {
    const q = searchQ.toLowerCase();
    return !q ? companies : companies.filter((c) => c.name.toLowerCase().includes(q));
  }, [companies, searchQ]);

  const pag = usePaginated(filtered);

  async function handleSave(data: CompanySaveData) {
    try {
      if (editing?.mode === 'edit') {
        await adminUpdateCompany(editing.company.id, data);
        addToast('Unternehmen aktualisiert.');
      } else {
        await adminCreateCompany(data);
        addToast('Unternehmen erstellt.');
      }
      setEditing(null);
      await onChanged();
    } catch { addToast('Fehler beim Speichern.', 'danger'); }
  }

  async function handleDelete(c: Company) {
    try {
      await adminDeleteCompany(c.id);
      addToast('Unternehmen gelöscht.');
      setConfirmDelete(null);
      await onChanged();
    } catch { addToast('Fehler beim Löschen.', 'danger'); }
  }

  if (editing) {
    return (
      <CompanyEditor
        initial={editing.mode === 'edit' ? editing.company : undefined}
        template={editing.mode === 'duplicate' ? editing.template : undefined}
        groups={groups}
        onSave={handleSave}
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <>
      <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
        <button className="btn btn-primary btn-sm" onClick={() => setEditing({ mode: 'new' })}>+ Unternehmen hinzufügen</button>
        <input type="search" className="form-control form-control-sm ms-auto" style={{ maxWidth: 280 }} placeholder="Suche nach Name…" value={searchQ} onChange={(e) => setSearchQ(e.target.value)} />
      </div>

      <div className="table-responsive">
        <table className="table table-hover align-middle mb-0" style={{ fontSize: '0.83rem' }}>
          <thead style={TH}>
            <tr>
              <th className="ps-3">Name</th>
              <th>Logo</th>
              <th>Std. Zeitraum</th>
              <th>Std. Anzahl</th>
              <th>Sitzungsdauer</th>
              <th>Präfixe / Nodes</th>
              <th className="text-end pe-3">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {pag.slice.map((c) => (
              <tr key={c.id}>
                <td className="ps-3 fw-semibold">
                  {c.name}
                  {c.groupId && (
                    <div>
                      <span className="badge bg-info-subtle text-info-emphasis mt-1" style={{ fontSize: '0.65rem' }} title="Notizen & Status werden in dieser Gruppe geteilt">
                        ⛓ {groups.find((g) => g.id === c.groupId)?.name ?? 'Gruppe'}
                      </span>
                    </div>
                  )}
                </td>
                <td>
                  {c.logoSvg ? (
                    <img src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(c.logoSvg)}`} alt={c.name} style={{ height: 28, width: 'auto', maxWidth: 44, objectFit: 'contain' }} />
                  ) : <span className="text-muted">—</span>}
                </td>
                <td>{PERIOD_LABELS[c.filterConfig.defaultPeriod]}</td>
                <td>{c.filterConfig.defaultCount}</td>
                <td>{c.filterConfig.sessionDurationDays === 1 ? '1 Tag' : `${c.filterConfig.sessionDurationDays} Tage`}</td>
                <td style={{ maxWidth: 200 }}>
                  {c.filterConfig.inboundPrefixes.length > 0 && (
                    <div className="text-truncate" title={c.filterConfig.inboundPrefixes.join(', ')}>
                      <span className="text-muted me-1" style={{ fontSize: '0.72rem' }}>↓Nr:</span>
                      {c.filterConfig.inboundPrefixes.join(', ')}
                    </div>
                  )}
                  {(c.filterConfig.outboundPrefixes ?? []).length > 0 && (
                    <div className="text-truncate" title={c.filterConfig.outboundPrefixes.join(', ')}>
                      <span className="text-muted me-1" style={{ fontSize: '0.72rem' }}>↑Nr:</span>
                      {c.filterConfig.outboundPrefixes.join(', ')}
                    </div>
                  )}
                  {c.filterConfig.incomingNodes.length > 0 && (
                    <div className="text-truncate" title={c.filterConfig.incomingNodes.join(', ')}>
                      <span className="text-muted me-1" style={{ fontSize: '0.72rem' }}>↓Node:</span>
                      {c.filterConfig.incomingNodes.join(', ')}
                    </div>
                  )}
                  {c.filterConfig.outgoingNodes.length > 0 && (
                    <div className="text-truncate" title={c.filterConfig.outgoingNodes.join(', ')}>
                      <span className="text-muted me-1" style={{ fontSize: '0.72rem' }}>↑Node:</span>
                      {c.filterConfig.outgoingNodes.join(', ')}
                    </div>
                  )}
                  {!c.filterConfig.inboundPrefixes.length && !(c.filterConfig.outboundPrefixes ?? []).length && !c.filterConfig.incomingNodes.length && !c.filterConfig.outgoingNodes.length && (
                    <span className="text-muted">Keine Filter</span>
                  )}
                </td>
                <td className="text-end pe-3">
                  <div className="d-flex gap-1 justify-content-end flex-wrap">
                    <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: '0.72rem' }} onClick={() => setEditing({ mode: 'edit', company: c })}>Bearbeiten</button>
                    <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: '0.72rem' }} title="Als Vorlage für ein neues Unternehmen verwenden" onClick={() => setEditing({ mode: 'duplicate', template: c })}>Duplizieren</button>
                    <button className="btn btn-sm btn-outline-danger" style={{ fontSize: '0.72rem' }} onClick={() => setConfirmDelete(c)}>Löschen</button>
                  </div>
                </td>
              </tr>
            ))}
            {pag.slice.length === 0 && (
              <tr><td colSpan={7} className="text-center text-muted py-4">{searchQ ? 'Keine Treffer' : 'Keine Unternehmen'}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <PaginationBar page={pag.page} totalPages={pag.totalPages} pageSize={pag.pageSize} total={pag.total} onPage={pag.setPage} onPageSize={pag.setPageSize} />

      {confirmDelete && (
        <ConfirmDialog
          title="Unternehmen löschen?"
          text={`„${confirmDelete.name}" wird gelöscht. Benutzer des Unternehmens verlieren ihre Filter-Sicht.`}
          onConfirm={() => handleDelete(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </>
  );
}
